import { isEqual } from 'es-toolkit';
import React, { createContext, memo, PropsWithChildren, Suspense, useContext, useMemo, useState } from 'react';
import { Color, Vector3 } from 'three';

import { formatLengthToArchitecturalString } from '@paragon/client-tools';
import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { LineSegment2D, Point2D } from '@paragon/geometry-library';

import fontPath from '../../../resources/roboto-regular.fnt';
import sdfPath from '../../../resources/roboto-regular.png';
import { useAppSelector } from '../../components/hooks';
import {
  convertFromLayoutToDesignCoordinates,
  createLayoutToDesignTransform,
  LAYOUT_TO_DESIGN_COORDINATES_MATRIX,
} from '../../library/layout-geometry/coordinates';
import { getHighVisibilityMode } from '../../store/selectors/getHighVisibilityMode';
import { isLabelTooTallForViewport, useAntiZoomCheck } from '../antiZoom';
import { Segment, Segments } from '../layout/Segments';
import { useCameraFrame } from '../layout/useCameraFrame';
import { SDFText } from '../text/SDFText';
import { SDFTextProvider } from '../text/SDFTextProvider';
import { DIMENSION_OPACITY, getDimensionLabelOptions, getDimensionLineColor } from './dimensionStyles';
import { calculateLabelPositions } from './DistanceDimension';
import { buildEndpointMarkSegments, CENTERED_LAYOUT_ENDPOINT_MARK, LAYOUT_ENDPOINT_MARK } from './endpointMarks';

const FONT_CONFIGURATION = { sdfPath, fontPath };

const dimensionContext = createContext({
  threeDimensional: false,
  highVisibilityMode: false,
});

export function OptimizedDistanceDimensionContainer(props: PropsWithChildren<{ threeDimensional: boolean }>) {
  const { threeDimensional } = props;
  const highVisibilityMode = useAppSelector(getHighVisibilityMode);
  const hiddenByZoomLevelIn2D = useAntiZoomCheck((level) => level > 15);
  // 6.6 is approximately the height of a label in world units:
  //   94px                                * 0.01                           * 7
  //   line height from roboto-regular.fnt * scale from SDFTextProvider.tsx * LABEL_SCALE below
  const hiddenByLabelHeightIn2D = useAntiZoomCheck((level) => isLabelTooTallForViewport(6.6, level));
  const useLowOpacityOutline = useAntiZoomCheck((level) => level > 10);
  const visible = threeDimensional || !hiddenByZoomLevelIn2D;
  const labelsVisible = visible && (threeDimensional || !hiddenByLabelHeightIn2D);

  const contextValue = useMemo(
    () => ({ threeDimensional, highVisibilityMode }),
    [threeDimensional, highVisibilityMode],
  );

  const segmentsProps = useMemo(
    () => ({
      transparent: true,
      opacity: DIMENSION_OPACITY,
      depthTest: threeDimensional,
      initialSize: 100,
      sizeIncrement: 100,
      color: getDimensionLineColor('standard', highVisibilityMode),
      visible,
    }),
    [threeDimensional, highVisibilityMode, visible],
  );

  return (
    <Suspense>
      <SDFTextProvider
        faceCamera={threeDimensional}
        depthTest={threeDimensional}
        fontPathRegular={FONT_CONFIGURATION}
        outlineOpacityMultiplier={
          useLowOpacityOutline ? LABEL_OUTLINE_LOW_OPACITY_MULTIPLIER : LABEL_OUTLINE_OPACITY_MULTIPLIER
        }
        visible={labelsVisible}
      >
        <Segments {...segmentsProps}>
          <dimensionContext.Provider value={contextValue}>{props.children}</dimensionContext.Provider>
        </Segments>
      </SDFTextProvider>
    </Suspense>
  );
}

export interface OptimizedDistanceDimensionProps {
  readonly distance: number;
  readonly start: Point2DInterface;
  readonly end: Point2DInterface;
  readonly elevation?: number;
  readonly withPerpendicularLabel?: boolean;
  readonly symmetricEndpointMarks?: boolean;
}

function propsAreEqual(first: OptimizedDistanceDimensionProps, second: OptimizedDistanceDimensionProps) {
  return (
    first.distance === second.distance &&
    first.elevation === second.elevation &&
    first.withPerpendicularLabel === second.withPerpendicularLabel &&
    first.symmetricEndpointMarks === second.symmetricEndpointMarks &&
    isEqual(first.start, second.start) &&
    isEqual(first.end, second.end)
  );
}

const LABEL_SCALE = new Vector3(7, 7, 7);

const _colorCache = new Map<number, Color>();
function getColor(hex: number) {
  let result = _colorCache.get(hex);

  if (!result) {
    result = new Color(hex);
    _colorCache.set(hex, result);
  }

  return result;
}
const LABEL_OUTLINE_WIDTH = 0.12;
const LABEL_OUTLINE_OPACITY_MULTIPLIER = 1;
const LABEL_OUTLINE_LOW_OPACITY_MULTIPLIER = 0.65;

const VISIBILITY_RANGE_3D = 900;

function ThreeDimensionalLabelVisibility({ children, labelPosition }: PropsWithChildren<{ labelPosition: Vector3 }>) {
  const [hiddenByDistance, setHiddenByDistance] = useState(false);
  const positionInDesignCoordinates = useMemo(
    () => labelPosition.clone().applyMatrix4(LAYOUT_TO_DESIGN_COORDINATES_MATRIX),
    [labelPosition],
  );

  useCameraFrame((_frustum, cameraPosition) => {
    const outsideRange = positionInDesignCoordinates.distanceTo(cameraPosition) > VISIBILITY_RANGE_3D;
    if (outsideRange !== hiddenByDistance) setHiddenByDistance(outsideRange);
  });

  return hiddenByDistance ? null : children;
}

/**
 * DistanceDimension with less features, but faster. Must be used within an `<OptimizedDistanceDimensionContainer />`.
 */
export const OptimizedDistanceDimension = memo((props: OptimizedDistanceDimensionProps) => {
  const { threeDimensional, highVisibilityMode } = useContext(dimensionContext);

  const labelOptions = getDimensionLabelOptions('standard', highVisibilityMode);

  const startPoint = Point2D.fromObject(props.start);
  const endPoint = Point2D.fromObject(props.end);
  const dimensionSegment = new LineSegment2D(startPoint, endPoint);
  const elevation = props.elevation ?? 0;

  const { labelOnePosition: labelPosition, rotation: labelRotation } = calculateLabelPositions(
    dimensionSegment.toSegmentObject(), // dimension segment
    {
      orientText: true, // always desired for dimensions
      perpendicularToDimension: props.withPerpendicularLabel,
      fontSize: 5, // close enough to the scale we're using; feel free to adjust
      antiZoomFactor: 1, // current usages don't care about zoom level; add a prop if you need this

      // for perpendicular labels a small offset is needed to prevent overlap with the dimension segment
      textFloat: props.withPerpendicularLabel ? 1 : undefined,
    },
  );

  const { ticks, extensionMarks } = buildEndpointMarkSegments(
    dimensionSegment,
    props.symmetricEndpointMarks ? CENTERED_LAYOUT_ENDPOINT_MARK : LAYOUT_ENDPOINT_MARK,
  );

  const inDesignCoordinates = (point: Point2D) => convertFromLayoutToDesignCoordinates(point.to3D(elevation)).toArray();

  const labelPositionVector = new Vector3(...labelPosition.toArray(), elevation);

  const labelTransform = createLayoutToDesignTransform(labelPositionVector, labelRotation, LABEL_SCALE);
  const label = (
    <SDFText
      matrixWorld={labelTransform}
      color={getColor(labelOptions.color)}
      opacity={labelOptions.opacity}
      outlineWidth={LABEL_OUTLINE_WIDTH}
      outlineOpacity={labelOptions.outlineOpacity}
      outlineColor={getColor(labelOptions.outlineColor)}
      alignX={props.withPerpendicularLabel ? 'left' : 'center'}
      alignY="middle"
      text={formatLengthToArchitecturalString(props.distance)}
      instanceKey={`dimension-text-${elevation}-${JSON.stringify(startPoint.toArray())}-${JSON.stringify(endPoint.toArray())}`}
    />
  );

  return (
    <>
      <Segment start={inDesignCoordinates(startPoint)} end={inDesignCoordinates(endPoint)} />
      {ticks.map((tick, index) => (
        <Segment key={`tick-${index}`} start={inDesignCoordinates(tick.start)} end={inDesignCoordinates(tick.end)} />
      ))}
      {extensionMarks.map((mark, index) => (
        <Segment key={`mark-${index}`} start={inDesignCoordinates(mark.start)} end={inDesignCoordinates(mark.end)} />
      ))}

      {threeDimensional ? (
        <ThreeDimensionalLabelVisibility labelPosition={labelPositionVector}>{label}</ThreeDimensionalLabelVisibility>
      ) : (
        label
      )}
    </>
  );
}, propsAreEqual);
